import React, { useEffect, useState } from "react";
import { VideoCamera } from "@phosphor-icons/react";
import { Link, Navigate } from "react-router-dom";
import { Page } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { useConsultModal } from "@/components/ConsultModal";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { ArrowRight, CalendarBlank, Coins, ChatCircleDots, Briefcase, GraduationCap, ShoppingBag, BookOpen, ClockClockwise, DownloadSimple, Package } from "@phosphor-icons/react";
import ProfileSection from "@/components/ProfileSection";
import TrailBreadcrumb from "@/components/TrailBreadcrumb";
import TrailBackPill from "@/components/TrailBackPill";
import PageLoader from "@/components/PageLoader";

// Sections users may have travelled through before landing here. Each entry
// names a destination the breadcrumb can link back to.
const MEMBER_TRAIL_SECTIONS = {
  therapists: { label: "Therapists", href: "/therapists" },
  mentors: { label: "Mentors", href: "/mentors" },
  shop: { label: "Shop", href: "/shop" },
  articles: { label: "Library", href: "/articles" },
  events: { label: "Events", href: "/events" },
  couch: { label: "Couch", href: "/couch" },
  forward: { label: "Forward", href: "/forward" },
};

const STATUS_TONE = {
  confirmed: { bg: "rgba(42, 92, 54, 0.08)", color: "var(--md-primary)", label: "Confirmed" },
  paid: { bg: "rgba(42, 92, 54, 0.08)", color: "var(--md-primary)", label: "Paid" },
  pending_payment: { bg: "rgba(244, 162, 97, 0.18)", color: "#A45A1A", label: "Awaiting payment" },
  pending: { bg: "rgba(244, 162, 97, 0.18)", color: "#A45A1A", label: "Pending" },
  cancelled: { bg: "rgba(176, 70, 70, 0.1)", color: "#B04646", label: "Cancelled" },
  completed: { bg: "rgba(91, 99, 96, 0.12)", color: "var(--md-text-soft)", label: "Completed" },
};

function fmtDate(d) {
  if (!d) return ",";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function MemberDashboard() {
  const { user, loading } = useAuth();
  const { open: openConsult } = useConsultModal();
  const { format } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [credits, setCredits] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderPage, setOrderPage] = useState(1);
  const [orderStatus, setOrderStatus] = useState("");
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderHasMore, setOrderHasMore] = useState(false);
  const ORDERS_PAGE_SIZE = 5;
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.allSettled([
      api.get("/bookings/me").then((r) => setBookings(r.data || [])),
      api.get("/consultations/me").then((r) => setConsultations(r.data || [])).catch(() => setConsultations([])),
      api.get("/credits/me").then((r) => setCredits(r.data || [])).catch(() => setCredits([])),
    ]).finally(() => setLoadingData(false));
  }, [user]);

  // Orders pagination/filter, re-fetch when page or status changes.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams({ page: String(orderPage), page_size: String(ORDERS_PAGE_SIZE) });
    if (orderStatus) params.set("status", orderStatus);
    api.get(`/orders/me?${params.toString()}`)
      .then((r) => {
        const d = r.data || {};
        setOrders(d.items || []);
        setOrderTotal(d.total || 0);
        setOrderHasMore(d.has_more || false);
      })
      .catch(() => { setOrders([]); setOrderTotal(0); setOrderHasMore(false); });
  }, [user, orderPage, orderStatus]);

  if (loading) return <Page><PageLoader /></Page>;
  if (!user) return <Navigate to="/login" replace />;

  const upcoming = bookings.filter((b) => ["confirmed", "paid", "pending_payment", "pending"].includes(b.status));
  const past = bookings.filter((b) => ["completed", "cancelled"].includes(b.status));
  const creditTotal = credits.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  const lifetimeSpend = bookings
    .filter((b) => b.payment_status === "paid")
    .reduce((s, b) => s + (Number(b.amount) || 0), 0);

  return (
    <Page>
      <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
        <div className="md-container">
          <TrailBackPill
            scope="md.member.trail"
            currentId="dashboard"
            sections={MEMBER_TRAIL_SECTIONS}
            className="mb-3"
          />
          <div className="md-label-eyebrow mb-3">Member Portal</div>
          <h1 className="text-4xl md:text-5xl font-serif mb-2">Welcome, {user.name.split(" ")[0]}</h1>
          <p className="text-base" style={{ color: "var(--md-text-soft)" }}>Your sessions, credits and journey, all in one place.</p>
          <TrailBreadcrumb
            scope="md.member.trail"
            current="My Dashboard"
            currentId="dashboard"
            homeLabel="Home"
            homeHref="/"
            sections={MEMBER_TRAIL_SECTIONS}
            className="mt-3"
          />

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="member-summary-grid">
            <SummaryCard icon={CalendarBlank} label="Upcoming sessions" value={upcoming.length} testid="summary-upcoming" />
            <SummaryCard icon={Coins} label="Session credits" value={creditTotal} testid="summary-credits" />
            <SummaryCard icon={ClockClockwise} label="Past sessions" value={past.length} testid="summary-past" />
            <SummaryCard icon={ChatCircleDots} label="Lifetime spend" value={format(lifetimeSpend)} testid="summary-spend" />
          </div>
        </div>
      </section>

      {/* Upcoming sessions */}
      <section className="md-section">
        <div className="md-container">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="md-label-eyebrow mb-1.5">Upcoming sessions</div>
              <h2 className="text-3xl font-serif">Your next conversations</h2>
            </div>
            <Link to="/therapists" className="btn-outline text-sm" data-testid="member-book-new">Book new <ArrowRight size={14} weight="bold" /></Link>
          </div>

          {(() => {
            if (loadingData) return <PageLoader sm />;
            if (upcoming.length === 0) {
              return (
                <EmptyState
                  icon={CalendarBlank}
                  title="No sessions yet"
                  text="When you're ready, book a free 20-minute call with Shreeja or browse the therapist directory."
                  actions={
                    <>
                      <button onClick={openConsult} className="btn-primary text-sm" data-testid="empty-consult-cta">Free Consultation <ArrowRight size={14} weight="bold" /></button>
                      <Link to="/therapists" className="btn-outline text-sm" data-testid="empty-browse-cta">Browse Therapists <ArrowRight size={14} weight="bold" /></Link>
                    </>
                  }
                />
              );
            }
            return (
              <div className="grid md:grid-cols-2 gap-5" data-testid="upcoming-list">
                {upcoming.map((b) => <BookingCard key={b.id} b={b} format={format} />)}
              </div>
            );
          })()}
        </div>
      </section>

      {/* My consultations */}
      {consultations.length > 0 && (
        <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
          <div className="md-container">
            <div className="md-label-eyebrow mb-1.5">My consultations</div>
            <h2 className="text-3xl font-serif mb-6">Your free consultation</h2>
            <div className="grid md:grid-cols-2 gap-5" data-testid="my-consultations-list">
              {consultations.map((c) => (
                <div key={c.id} className="md-card" data-testid={`consultation-card-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="md-tag text-[10px]" style={{ textTransform: "capitalize" }}>{c.division}</span>
                    <span className="md-tag text-[10px]" style={{ textTransform: "capitalize" }}>{c.status}</span>
                  </div>
                  <div className="text-xl font-serif mt-3">Free consultation with Shreeja</div>
                  <div className="mt-2 text-sm" style={{ color: "var(--md-text-soft)" }}>
                    {c.date ? new Date(c.date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) : ""} at {c.time_slot}
                  </div>
                  {c.meet_link ? (
                    <a href={c.meet_link} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm mt-4" data-testid="consultation-meet-link">
                      <VideoCamera size={14} weight="bold" /> Join Google Meet
                    </a>
                  ) : (
                    <p className="text-xs mt-4" style={{ color: "var(--md-text-muted)" }}>
                      Your Google Meet link will appear here once scheduled.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past sessions */}
      {past.length > 0 && (
        <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
          <div className="md-container">
            <div className="md-label-eyebrow mb-1.5">Session history</div>
            <h2 className="text-3xl font-serif mb-6">Past sessions</h2>
            <div className="md-card overflow-x-auto" data-testid="past-table">
              <table className="admin-table" style={{ minWidth: 640 }}>
                <thead>
                  <tr><th>Date</th><th>Provider</th><th>Type</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {past.map((b) => (
                    <tr key={b.id}>
                      <td>{fmtDate(b.date || b.created_at)}</td>
                      <td>{b.provider_name || ","}</td>
                      <td style={{ textTransform: "capitalize" }}>{b.session_type || "Session"}</td>
                      <td>{format(b.amount || 0)}</td>
                      <td><StatusPill status={b.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Order history & downloads */}
      <section className="md-section">
        <div className="md-container">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="md-label-eyebrow mb-1.5">Shop history</div>
              <h2 className="text-3xl font-serif">Orders &amp; downloads</h2>
            </div>
            <Link to="/shop" className="btn-outline text-sm" data-testid="member-shop-cta">Browse shop <ArrowRight size={14} weight="bold" /></Link>
          </div>

          {/* Status filter chips */}
          {(orderTotal > 0 || orderStatus) && (
            <div className="orders-filter-row" data-testid="orders-filter">
              {[
                { k: "", label: "All" },
                { k: "paid", label: "Paid" },
                { k: "pending", label: "Pending" },
              ].map((opt) => (
                <button
                  key={opt.k || "all"}
                  type="button"
                  onClick={() => { setOrderStatus(opt.k); setOrderPage(1); }}
                  className={`orders-filter-chip ${orderStatus === opt.k ? "active" : ""}`}
                  data-testid={`orders-filter-${opt.k || "all"}`}
                >{opt.label}</button>
              ))}
              <span className="orders-filter-count" data-testid="orders-total-count">{orderTotal} order{orderTotal === 1 ? "" : "s"}</span>
            </div>
          )}

          {(() => {
            if (loadingData) return <PageLoader sm />;
            if (orders.length === 0 && !orderStatus) {
              return (
                <EmptyState
                  icon={Package}
                  title="No orders yet"
                  text="Your digital purchases will appear here. Books, journals and decks come with re-downloadable links."
                  actions={<Link to="/shop" className="btn-primary text-sm" data-testid="empty-shop-cta">Visit the shop <ArrowRight size={14} weight="bold" /></Link>}
                />
              );
            }
            if (orders.length === 0) {
              return (
                <p className="text-sm" style={{ color: "var(--md-text-muted)" }} data-testid="orders-empty-filtered">
                  No orders match this filter. <button type="button" onClick={() => setOrderStatus("")} className="underline" data-testid="orders-clear-filter">Clear filter</button>
                </p>
              );
            }
            return (
              <>
                <div className="space-y-4" data-testid="orders-list">
                  {orders.map((o) => <OrderCard key={o.id} o={o} format={format} />)}
                </div>
                {(orderPage > 1 || orderHasMore) && (
                  <div className="orders-pager" data-testid="orders-pager">
                    <button
                      type="button"
                      disabled={orderPage <= 1}
                      onClick={() => setOrderPage((p) => Math.max(1, p - 1))}
                      className="btn-outline text-sm"
                      data-testid="orders-prev"
                    >← Previous</button>
                    <span className="text-sm" style={{ color: "var(--md-text-muted)" }} data-testid="orders-page-label">Page {orderPage}</span>
                    <button
                      type="button"
                      disabled={!orderHasMore}
                      onClick={() => setOrderPage((p) => p + 1)}
                      className="btn-outline text-sm"
                      data-testid="orders-next"
                    >Next →</button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Credits + quick actions */}
      <section className="md-section">
        <div className="md-container grid lg:grid-cols-3 gap-6">
          <div className="md-card lg:col-span-2" data-testid="credits-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="how-glass-ico" style={{ background: "var(--md-bg-alt)", borderColor: "var(--md-border)" }}>
                <Coins size={24} weight="duotone" style={{ color: "var(--md-primary)" }} />
              </div>
              <div>
                <div className="md-label-eyebrow">Session credits</div>
                <h3 className="text-2xl font-serif">{creditTotal} active</h3>
              </div>
            </div>
            {credits.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--md-text-muted)" }}>No credits yet. Credits are issued by the team for programmes, retainers and referrals.</p>
            ) : (
              <ul className="space-y-2 mt-2">
                {credits.slice(0, 5).map((c) => (
                  <li key={c.id} className="flex items-center justify-between text-sm py-2 border-t" style={{ borderColor: "var(--md-border)" }} data-testid={`credit-${c.id}`}>
                    <span>{c.reason || "Granted credit"} · <span style={{ color: "var(--md-text-muted)" }}>expires {fmtDate(c.expires_at)}</span></span>
                    <strong style={{ color: "var(--md-primary)" }}>+{c.amount}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="md-card" data-testid="quick-actions">
            <div className="md-label-eyebrow mb-3">Quick actions</div>
            <div className="space-y-2.5">
              <QuickLink to="/therapists" icon={Briefcase} label="Find a Therapist" />
              <QuickLink to="/mentors" icon={GraduationCap} label="Find a Mentor" />
              <QuickLink to="/couch/pricing" icon={BookOpen} label="Sessions & Pricing" />
              <QuickLink to="/shop" icon={ShoppingBag} label="Shop" />
              <button type="button" onClick={openConsult} className="quick-link w-full text-left" data-testid="quick-consult">
                <ChatCircleDots size={18} weight="duotone" style={{ color: "var(--md-primary)" }} />
                <span>Book a Free Consultation</span>
                <ArrowRight size={14} weight="bold" style={{ marginLeft: "auto", opacity: 0.6 }} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Account profile section (photo upload + password) */}
      <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
        <div className="md-container">
          <div className="md-label-eyebrow mb-1.5">Account</div>
          <h2 className="text-3xl font-serif mb-6">Your profile</h2>
          <ProfileSection />
        </div>
      </section>
    </Page>
  );
}

function SummaryCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="md-card" data-testid={testid}>
      <Icon size={22} weight="duotone" style={{ color: "var(--md-primary)" }} />
      <div className="text-2xl font-serif mt-2" style={{ color: "var(--md-primary)" }}>{value}</div>
      <div className="md-label-eyebrow mt-1">{label}</div>
    </div>
  );
}

function BookingCard({ b, format }) {
  return (
    <Link
      to={`/dashboard/bookings/${b.id}?trail=dashboard`}
      className="md-card md-card-hover block"
      data-testid={`booking-card-${b.id}`}
      aria-label={`View booking with ${b.provider_name || "MindDhara"} on ${fmtDate(b.date)}`}
    >
      <div className="flex items-center gap-3 mb-3">
        {b.provider_photo ? (
          <img src={b.provider_photo} alt={b.provider_name} className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "var(--md-bg-alt)" }}>
            <Briefcase size={20} weight="duotone" style={{ color: "var(--md-primary)" }} />
          </div>
        )}
        <div>
          <h3 className="font-serif text-lg">{b.provider_name || "MindDhara session"}</h3>
          <p className="text-xs" style={{ color: "var(--md-text-muted)", textTransform: "capitalize" }}>
            {b.session_type || "Session"} · {b.provider_type}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: "var(--md-border)" }}>
        <div>
          <div className="text-xs" style={{ color: "var(--md-text-muted)" }}>{fmtDate(b.date)} · {b.time_slot}</div>
          <div className="text-base font-medium mt-0.5" style={{ color: "var(--md-primary)" }}>{format(b.amount || 0)}</div>
        </div>
        <StatusPill status={b.status} />
      </div>
    </Link>
  );
}

function OrderCard({ o, format }) {
  const items = o.items || [];
  const paid = o.payment_status === "paid";
  return (
    <article className="md-card" data-testid={`order-card-${o.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono" style={{ color: "var(--md-text-muted)" }}>Order {o.id?.slice(0, 12)}</div>
          <div className="text-sm mt-0.5" style={{ color: "var(--md-text-soft)" }}>{fmtDate(o.created_at || o.paid_at)}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-base font-medium" style={{ color: "var(--md-primary)" }}>{format(o.total || 0)}</div>
          <StatusPill status={paid ? "paid" : o.status} />
        </div>
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((it, idx) => (
          <li key={`${o.id}-${idx}`} className="flex items-center justify-between gap-3 pt-3 border-t" style={{ borderColor: "var(--md-border)" }} data-testid={`order-${o.id}-item-${idx}`}>
            <div className="flex items-center gap-3 min-w-0">
              {it.image_url ? (
                <img src={it.image_url} alt={it.name} className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "var(--md-bg-alt)" }}>
                  <ShoppingBag size={18} weight="duotone" style={{ color: "var(--md-primary)" }} />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{it.name || "Digital product"}</div>
                <div className="text-xs" style={{ color: "var(--md-text-muted)" }}>Qty {it.qty || it.quantity || 1} · {format(it.price || 0)}</div>
              </div>
            </div>
            {(() => {
              if (paid && it.download_url) {
                return (
                  <a href={it.download_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs" data-testid={`download-${o.id}-${idx}`}>
                    <DownloadSimple size={12} weight="bold" /> Download
                  </a>
                );
              }
              if (paid) {
                return <span className="text-xs" style={{ color: "var(--md-text-muted)" }}>Link arrives via email</span>;
              }
              return <span className="text-xs" style={{ color: "var(--md-text-muted)" }}>Awaiting payment</span>;
            })()}
          </li>
        ))}
      </ul>
    </article>
  );
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || { bg: "rgba(91, 99, 96, 0.12)", color: "var(--md-text-soft)", label: status };
  return (
    <span style={{
      background: tone.bg,
      color: tone.color,
      padding: "0.25rem 0.6rem",
      borderRadius: "999px",
      fontSize: "0.7rem",
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    }} data-testid={`status-pill-${status}`}>{tone.label}</span>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="quick-link" data-testid={`quick-${to.replace(/\//g, "-")}`}>
      <Icon size={18} weight="duotone" style={{ color: "var(--md-primary)" }} />
      <span>{label}</span>
      <ArrowRight size={14} weight="bold" style={{ marginLeft: "auto", opacity: 0.6 }} />
    </Link>
  );
}

function EmptyState({ icon: Icon, title, text, actions }) {
  return (
    <div className="md-card text-center" style={{ padding: "3rem 1.5rem" }} data-testid="empty-state">
      <div className="how-glass-ico mx-auto mb-4" style={{ background: "var(--md-bg-alt)", borderColor: "var(--md-border)" }}>
        <Icon size={28} weight="duotone" style={{ color: "var(--md-primary)" }} />
      </div>
      <h3 className="text-xl font-serif mb-2">{title}</h3>
      <p className="text-sm max-w-md mx-auto" style={{ color: "var(--md-text-soft)" }}>{text}</p>
      {actions && <div className="flex gap-3 justify-center mt-5 flex-wrap">{actions}</div>}
    </div>
  );
}
